import { INoteContent, INoteItem, INoteItems, TEmbedArticles } from "../../types/INote";
import parse from "html-react-parser";
import Youtube from "../Embeds/Youtube";
import EmbedContainer from "../Embeds/EmbedContainer";
import ImageRTC from "../Embeds/ImageRTC";
import Twitter from "../Embeds/Twitter";
import Spotify from "../Embeds/Spotify";
import Instagram from "../Embeds/Instagram";
import Facebook from "../Embeds/Facebook";
import OnnVideo from "../Embeds/OnnVideo";
import TikTok from "../Embeds/TokTok";
import Telegram from "../Embeds/Telegram";
import Imgur from "../Embeds/Imgur";
import UomArticleRelated from "../Embeds/UomArticleRelated";
import Windy from "../Embeds/Windy";
import GoogleMaps from "../Embeds/GoogleMaps";
import styles from "./index.module.scss";
import React from "react";
import Flourish from "../Embeds/Flourish";

export default function NoteContent({
  noteId,
  content,
  embedArticles,
  isMobile,
}: {
  noteId: string;
  content: INoteContent;
  embedArticles?: TEmbedArticles;
  isMobile: boolean;
}): JSX.Element {
  const paragraphContent = (item: INoteItem, idx: number) => {
    switch (item.type) {
      case "externalLink":
        if (item.url && item.text) {
          return (
            <a className={styles.externalLink} href={item.url} rel={item.rel || ""} key={`note-externalLink-${idx}`}>
              {item.text}
            </a>
          );
        }
        return <></>;

      default:
        if (item.text) {
          return <React.Fragment key={`note-text-${idx}`}>{parse(item.text)}</React.Fragment>;
        }
        break;
    }
  };

  const uomImage = (item: INoteItem, idx: number) => {
    return (
      <EmbedContainer key={`note-embed-${item.imageId}-${idx}`}>
        {item.imageId && <ImageRTC imageId={item.imageId.toString() || ""} title={item.text || ""} />}
      </EmbedContainer>
    );
  };

  return (
    <>
      {content.elements.map((element: INoteItems, idxC: number) => {
        switch (element.type) {
          case "paragraph":
            const containUomImage = element.items?.find((item: INoteItem) => item.type === "uomImage") !== undefined;
            if (containUomImage) {
              return (
                <React.Fragment key={`note-uomImage-${idxC}`}>
                  {element.items?.map((item: INoteItem, idx: number) => {
                    return uomImage(item, idx);
                  })}
                </React.Fragment>
              );
            }
            return (
              <p key={`note-paragraph-${idxC}`}>
                {element.items?.map((item: INoteItem, idx: number) => {
                  return paragraphContent(item, idx);
                })}
              </p>
            );
          case "unorderedList":
            return (
              <ul className={styles.list} key={`notes-unordered-list-item-${noteId}`}>
                {makeList(`notes-list-item-${noteId}`, element.positions)}
              </ul>
            );
            break;
          case "orderedList":
            return (
              <ul className={styles.orderedList} key={`notes-ordered-list-item-${noteId}`}>
                {makeList(`notes-ordered-list-item-${noteId}`, element.positions)}
              </ul>
            );
            break;
          case "blockquote":
            return (
              <blockquote
                className={isMobile ? styles.blockquoteMobile : styles.blockquote}
                data-e="note-blockquote"
                key={`note-blockquote-${noteId}`}
              >
                {parseToHTML(element.items)}
              </blockquote>
            );
            break;

          case "subTitle":
            return (
              <h3
                className={isMobile ? styles.subTitleMobile : styles.subTitle}
                data-e="note-subTitle"
                key={`note-subTitle-${noteId}`}
              >
                {parseToHTML(element.items)}
              </h3>
            );
            break;

          case "interviewQuestion":
            return (
              <h4
                className={isMobile ? styles.interviewQuestionMobile : styles.interviewQuestion}
                data-e="note-interviewQuestion"
                key={`note-interviewQuestion-${noteId}`}
              >
                {parseToHTML(element.items)}
              </h4>
            );
            break;
          case "embed":
          case "uomEmbed":
            return element.items?.map((item: INoteItem, idx: number) => {
              switch (item.type) {
                case "onnetworkVideo":
                  return (
                    <EmbedContainer key={`note-onnetworkVideo-${item.id}-${idx}`}>
                      {item.id && (
                        <OnnVideo
                          id={item.id}
                          videoType={item.videoType ? item.videoType : ""}
                          description={item.description ? item.description : ""}
                          podcast={item.podcast}
                        />
                      )}
                    </EmbedContainer>
                  );

                case "youtube":
                  return (
                    <EmbedContainer key={`note-youtube-${item.id}`}>
                      {item.id && <Youtube id={item.id} />}
                    </EmbedContainer>
                  );

                case "twitter":
                  return (
                    <EmbedContainer key={`note-twitter-${item.id}`}>
                      {item.id && <Twitter id={item.id} />}
                    </EmbedContainer>
                  );

                case "spotify":
                  return (
                    <EmbedContainer key={`note-spotify-${item.id}`}>
                      {item.id && <Spotify id={item.id} />}
                    </EmbedContainer>
                  );

                case "instagram":
                  return (
                    <EmbedContainer key={`note-instagram-${item.id}`}>
                      {item.id && <Instagram id={item.id} />}
                    </EmbedContainer>
                  );

                case "facebook":
                  return (
                    <EmbedContainer key={`note-facebook-${item.id}`}>
                      {item.id && <Facebook id={item.id} />}
                    </EmbedContainer>
                  );

                case "tiktok":
                  return (
                    <EmbedContainer key={`note-tiktok-${item.id}`}>
                      {item.id && <TikTok id={item.id} nick={item.nick || ""} />}
                    </EmbedContainer>
                  );

                case "telegram":
                  return (
                    <EmbedContainer key={`note-telegram-${item.id}`}>
                      {item.id && <Telegram id={item.id} />}
                    </EmbedContainer>
                  );

                case "googleMaps":
                  return (
                    <EmbedContainer key={`note-googleMaps-${item.id}`}>
                      {item.id && <GoogleMaps id={item.id} googleMapsType={item.googleMapsType || ""} />}
                    </EmbedContainer>
                  );

                case "imgur":
                  return (
                    <EmbedContainer key={`note-imgur-${item.id}`}>
                      {item.id && <Imgur id={item.id} text={item.text || ""} />}
                    </EmbedContainer>
                  );

                case "uomArticle":
                case "uomArticleRelated":
                  return (
                    <EmbedContainer key={`note-uomArticle-${item.articleId}-${idx}`}>
                      {item.articleId && (
                        <UomArticleRelated articleId={item.articleId.toString()} embedArticles={embedArticles} />
                      )}
                    </EmbedContainer>
                  );

                case "windy":
                  return (
                    <EmbedContainer key={`note-windy-${item.src}`}>
                      {item.src && <Windy url={item.src} />}
                    </EmbedContainer>
                  );

                case "flourish":
                  return (
                    <EmbedContainer key={`note-flourish-${item.src}`}>
                      {item.src && <Flourish src={item.src} />}
                    </EmbedContainer>
                  );
              }
            });
        }
      })}
    </>
  );
}

const parseToHTML = (items?: INoteItem[]): (string | JSX.Element | JSX.Element[])[] | undefined => {
  return items?.map((item: INoteItem) => parse(item?.text || ""));
};

const makeList = (key: string, positions?: INoteItem[][]): (string | JSX.Element | JSX.Element[])[] | undefined => {
  return positions?.map((item: INoteItem[], idx: number) => <li key={`${key}-${idx}`}>{parseToHTML(item)}</li>);
};
